import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Toaster } from "@/components/ui/sonner";
// Landing is the entry route — keep it eager for fastest first paint.
import Landing from "@/pages/Landing";
import AppLayout from "@/components/AppLayout";
import PublicLayout from "@/components/PublicLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import InstallPWAButton from "@/components/InstallPWAButton";
import WishlistTooltip from "@/components/WishlistTooltip";
import { I18nProvider } from "@/i18n/I18nProvider";
import { registerServiceWorker } from "@/lib/registerServiceWorker";

// Code-split: every secondary route loads its JS chunk only when visited.
const Auth = lazy(() => import("@/pages/Auth"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Community = lazy(() => import("@/pages/Community"));
const Marketplace = lazy(() => import("@/pages/Marketplace"));
const Learning = lazy(() => import("@/pages/Learning"));
const Events = lazy(() => import("@/pages/Events"));
const Leadership = lazy(() => import("@/pages/Leadership"));
const Wallet = lazy(() => import("@/pages/Wallet"));
const Rewards = lazy(() => import("@/pages/Rewards"));
const Profile = lazy(() => import("@/pages/Profile"));
const Orders = lazy(() => import("@/pages/Orders"));
const Admin = lazy(() => import("@/pages/Admin"));
const Earnings = lazy(() => import("@/pages/Earnings"));
const Recognition = lazy(() => import("@/pages/Recognition"));
const Guide = lazy(() => import("@/pages/Guide"));
const Cart = lazy(() => import("@/pages/Cart"));
const Join = lazy(() => import("@/pages/Join"));
const Search = lazy(() => import("@/pages/Search"));
const RechargeWallet = lazy(() => import("@/pages/RechargeWallet"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const Categories = lazy(() => import("@/pages/Categories"));
const FounderCard = lazy(() => import("@/pages/FounderCard"));
const LeaderCard = lazy(() => import("@/pages/LeaderCard"));
const LeadersDirectory = lazy(() => import("@/pages/LeadersDirectory"));
const PrivacyPolicy = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/legal/TermsOfService"));
const AccountDeletion = lazy(() => import("@/pages/legal/AccountDeletion"));
const AboutShaktiWings = lazy(() => import("@/pages/AboutShaktiWings"));
const CatalogExecutive = lazy(() => import("@/pages/CatalogExecutive"));
const ProductShortRedirect = lazy(() => import("@/pages/ProductShortRedirect"));

// Skeleton shown while a route chunk downloads (sub-second on 4G post-cache)
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-pearl to-amethyst-50" data-testid="route-loading">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-magenta-200 border-t-magenta-600 animate-spin" />
        <p className="text-xs text-amethyst-600 font-medium">Loading…</p>
      </div>
    </div>
  );
}

function Private({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-amethyst-600">Loading…</div>;
  if (!user) return <Navigate to={`/auth?next=${encodeURIComponent(loc.pathname)}`} replace />;
  // Catalog Executives are restricted to their own catalog console only
  if (user.role === "catalog_executive" && !loc.pathname.startsWith("/app/catalog")) {
    return <Navigate to="/app/catalog" replace />;
  }
  return children;
}

// Renders AppLayout sidebar for logged-in users, PublicLayout header for guests
function SmartLayout() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-amethyst-600">Loading…</div>;
  return user ? <AppLayout /> : <PublicLayout />;
}

function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);
  return (
    <div className="App">
      <ErrorBoundary>
        <I18nProvider>
          <AuthProvider>
            <CartProvider>
              <BrowserRouter>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/join" element={<Join />} />
                  <Route path="/signup" element={<Auth />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/guide" element={<Guide />} />
                  <Route path="/founder-card" element={<FounderCard />} />
                  <Route path="/leader/:slug" element={<LeaderCard />} />
                  <Route path="/leaders" element={<LeadersDirectory />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/account-deletion" element={<AccountDeletion />} />
                  <Route path="/about-shakti-wings" element={<AboutShaktiWings />} />
                  <Route path="/p/:code" element={<ProductShortRedirect />} />
                  {/* Public sections — guests can browse, actions prompt login */}
                  <Route element={<SmartLayout />}>
                    <Route path="/marketplace" element={<Marketplace />} />
                    <Route path="/categories" element={<Categories />} />
                    <Route path="/search" element={<Search />} />
                    <Route path="/wallet/recharge" element={<RechargeWallet />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/events" element={<Events />} />
                    <Route path="/community" element={<Community />} />
                    <Route path="/leadership" element={<Leadership />} />
                    <Route path="/earnings" element={<Earnings />} />
                    <Route path="/recognition" element={<Recognition />} />
                  </Route>
                  {/* Authed-only sections */}
                  <Route element={<Private><AppLayout /></Private>}>
                    <Route path="/app" element={<Dashboard />} />
                    <Route path="/app/learning" element={<Learning />} />
                    <Route path="/app/wallet" element={<Wallet />} />
                    <Route path="/app/rewards" element={<Rewards />} />
                    <Route path="/app/wishlist" element={<Wishlist />} />
                    <Route path="/app/orders" element={<Orders />} />
                    <Route path="/app/profile" element={<Profile />} />
                    <Route path="/app/admin" element={<Admin />} />
                    <Route path="/app/catalog" element={<CatalogExecutive />} />
                  </Route>
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
                {/* Mounted INSIDE BrowserRouter so useNavigate() has Router context */}
                <WishlistTooltip />
              </BrowserRouter>
              <InstallPWAButton />
              <Toaster position="top-right" richColors />
            </CartProvider>
          </AuthProvider>
        </I18nProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
